/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable sort-keys */
import {
   ChangeDetectionStrategy,
   ChangeDetectorRef,
   Component,
   EventEmitter,
   Input,
   OnInit,
   Output,
   ViewEncapsulation,
} from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { CallState } from '@lcs/shared/api';
import { ScheduleDetailInput, ScheduleInput } from 'generated/graphql-types';
import { Subscription } from 'rxjs';
import { accountPhoneNumbersRoute } from 'src/app/app-routing.module';
import { TwoColumnLayoutService } from 'src/app/common-components/column-layouts/two-column-list-layout/services/selected-id.service';
import { DetailView } from 'src/app/common-components/detail/detail-view';
import { SchedulePrivileges } from '../../schedule-register/models/schedule-privileges';
import { ScheduleDetailSaveEvent } from '../models/schedule-detail-form-submit-event';
import { ScheduleDetailView } from '../models/schedule-detail-view.model';
import { ScheduleDetailFormService } from '../services/schedule-detail-form.service';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    selector: 'ndt-schedule-detail-view',
    styleUrls: ['./schedule-detail-view.component.scss'],
    templateUrl: './schedule-detail-view.component.html',
    standalone: false
})
export class ScheduleDetailViewComponent extends DetailView implements OnInit {
   @Input() get schedule(): ScheduleDetailView | null {
      return this._schedule;
   }

   set schedule(value: ScheduleDetailView | null) {
      this._schedule = value;
      if (value) {
         this.formService.updateSchedule(value);
      }
   }

   @Input() set schedulePrivileges(privileges: SchedulePrivileges) {
      this._schedulePrivileges = privileges;
      if (!privileges?.canEdit) {
         this.form.disable();
      }
   }

   get schedulePrivileges(): SchedulePrivileges {
      return this._schedulePrivileges;
   }

   @Input() callState: CallState;

   @Output() formSaveCanceled = new EventEmitter<void>();

   @Output() formSubmitted = new EventEmitter<ScheduleDetailSaveEvent>();

   @Output() deleteSchedule = new EventEmitter<{
      scheduleId: number;
      name: string | null;
   }>();

   @Output() addScheduleHour = new EventEmitter<void>();

   @Output() addClosedException = new EventEmitter<void>();

   highlightedRow: number | null = null;

   form: UntypedFormGroup;

   shouldShowDetailView = true;

   columns = ['phoneNumber', 'displayName', 'status'];

   private _schedule: ScheduleDetailView | null;

   private _schedulePrivileges: SchedulePrivileges;

   private showDetailViewSubscription: Subscription;

   truncatedAccountName: string;

   constructor(
      public formService: ScheduleDetailFormService,
      private twoColumnLayoutService: TwoColumnLayoutService,
      private cdRef: ChangeDetectorRef
   ) {
      super();
      this.form = this.formService.form;
   }

   ngOnInit(): void {
      this.showDetailViewSubscription = this.twoColumnLayoutService.shouldShowDetailView$.subscribe((shouldShow) => {
         this.shouldShowDetailView = shouldShow;
         this.cdRef.markForCheck();
      });

      this.twoColumnLayoutService.deleteSchedule$.subscribe(() => {
         this.onDeleteClick();
      });
   }

   onSubmit(): void {
      const submitEvent = this.getFormSubmittedEvent();
      if (submitEvent) {
         this.formSubmitted.emit(submitEvent);
      }
   }

   onCancel(): void {
      this.formSaveCanceled.emit();
   }

   canDelete(): boolean {
      return this._schedulePrivileges.canDelete;
   }

   onDeleteClick(): void {
      if (this.schedule) {
         this.deleteSchedule.emit({
            name: this.schedule.name,
            scheduleId: this.schedule.scheduleId,
         });
      }
   }

   onAddScheduleHourClick(): void {
      this.addScheduleHour.emit();
   }

   getRouterLink(id: number) {
      return `/${accountPhoneNumbersRoute}/${id}`;
   }

   onAddClosedExceptionClick(): void {
      this.addClosedException.emit();
   }

   private getFormSubmittedEvent(): ScheduleDetailSaveEvent | null {
      if (!this.formService.form.dirty || !this.schedule) {
         return null;
      }
      const schedule = this.formService.getValue(this.schedule.scheduleId);
      let scheduleFields: (keyof ScheduleInput)[] = [];
      scheduleFields = this.formService.addFieldForDirtyControl(scheduleFields, 'name');
      scheduleFields = this.formService.addFieldForDirtyControl(scheduleFields, 'description');
      scheduleFields = this.formService.addFieldForDirtyControl(scheduleFields, 'scheduleHours', 'scheduleDetails');
      scheduleFields = this.formService.addFieldForDirtyControl(scheduleFields, 'closedExceptions');

      const scheduleDetailFields: (keyof ScheduleDetailInput)[] | null = [];

      return { schedule, scheduleDetailFields, scheduleFields };
   }
}
